// noinspection DuplicatedCode
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { Colors } from "../../../assets/Colors";
import {
  Card,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useMutation } from "@tanstack/react-query";
import { fetchPost } from "../../../fetchers";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import DataTable from "react-data-table-component";
import debounce from "lodash/debounce";
import { BiSearch, BiRefresh, BiFilter, BiDetail } from "react-icons/bi";
import { BsFileEarmarkExcelFill } from "react-icons/bs";
import { FaFileInvoice } from "react-icons/fa";
import { MdClose } from "react-icons/md";

const MonitoringInvoiceQuotation = () => {
  const navigate = useNavigate();

  // State untuk modal detail
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    data: null,
  });

  // Initial state hanya dengan filter bulan
  let initialState = {
    Bulan: new Date(),
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations,
  );

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [count, setCount] = useState(maxPageGlobal);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasData, setHasData] = useState(false);

  const pageRef = useRef(1);
  const countRef = useRef(maxPageGlobal);
  const searchRef = useRef("");

  const mutationData = useMutation((params) =>
    fetchPost(`GetMonitoringInvoice`, params),
  );

  // Fungsi untuk membuka modal detail
  const handleDetailClick = useCallback((row, e) => {
    if (e) e.stopPropagation();
    setDetailModal({
      isOpen: true,
      data: row,
    });
  }, []);

  // Fungsi untuk menutup modal detail
  const handleCloseDetail = useCallback(() => {
    setDetailModal({
      isOpen: false,
      data: null,
    });
  }, []);

  // Fungsi untuk melakukan client-side search
  const performClientSideSearch = useCallback((searchTerm, dataToSearch) => {
    if (!searchTerm.trim()) {
      return dataToSearch;
    }

    const lowercasedSearch = searchTerm.toLowerCase();

    return dataToSearch.filter((item) => {
      return (
        (item.invoice_no &&
          item.invoice_no.toLowerCase().includes(lowercasedSearch)) ||
        (item.quotation_no &&
          item.quotation_no.toLowerCase().includes(lowercasedSearch)) ||
        (item.klien_name &&
          item.klien_name.toLowerCase().includes(lowercasedSearch)) ||
        (item.work_desc &&
          item.work_desc.toLowerCase().includes(lowercasedSearch)) ||
        (item.contract_no &&
          item.contract_no.toLowerCase().includes(lowercasedSearch)) ||
        (item.status && item.status.toLowerCase().includes(lowercasedSearch))
      );
    });
  }, []);

  // Debounced search untuk client-side
  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        searchRef.current = searchValue;

        if (!allData.length) return;

        const filtered = performClientSideSearch(searchValue, allData);
        setFilteredData(filtered);
        setCurrentPage(1);
        pageRef.current = 1;

        if (searchValue) {
          toast.info(`Menemukan ${filtered.length} data`, {
            position: "top-center",
            autoClose: 1000,
          });
        }
      }, 300),
    [allData, performClientSideSearch],
  );

  // Cleanup debounce
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Fetch data dari API
  const fetchData = useCallback(() => {
    setIsLoading(true);

    const selectedDate = new Date(values.Bulan);
    const bulan = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
    const tahun = selectedDate.getFullYear().toString();

    // Satu periode ditarik UTUH sekali jalan, bukan sepotong sebesar ukuran halaman
    // tampilan. Halaman ini memakai paginationServer={false}: paginasi, kotak Cari,
    // dan ekspor semuanya bekerja di atas allData, sementara handlePageChange hanya
    // menggeser state tanpa fetch ulang. Versi lama mengirim countRef (default 10)
    // lalu memberi DataTable paginationTotalRows={filteredData.length}, sehingga
    // tabel menyimpulkan cuma ada satu halaman — 10 baris pertama itulah satu-satunya
    // yang pernah bisa dilihat, dicari, atau diekspor.
    const apiParams = {
      start: 0,
      count: 5000,
      search: searchRef.current,
      bulan: bulan,
      tahun: tahun,
    };

    console.log("Mengirim parameter ke API:", apiParams);

    toast.info("Memuat data Invoice...", {
      position: "top-center",
      autoClose: 1500,
      hideProgressBar: false,
    });

    mutationData.mutate(apiParams, {
      onSuccess: (response) => {
        console.log("Response dari API:", response);

        const data = response?.data || [];
        setAllData(data);
        setFilteredData(data);
        setHasData(data.length > 0);

        toast.success(`${data.length} data berhasil dimuat!`, {
          position: "top-center",
          autoClose: 1000,
        });
        setIsLoading(false);
        setIsInitialLoad(false);

        setSearchValue("");
        searchRef.current = "";
      },
      onError: (error) => {
        console.error("Error detail:", error);
        console.error("Error response:", error.response);

        toast.error(`Gagal memuat data: ${error.message}`, {
          position: "top-center",
          autoClose: 2000,
        });
        setIsLoading(false);
        setIsInitialLoad(false);
        setHasData(false);
      },
    });
  }, [mutationData, values.Bulan]);

  // Update filtered data ketika allData berubah
  useEffect(() => {
    if (allData.length > 0 && searchRef.current) {
      const filtered = performClientSideSearch(searchRef.current, allData);
      setFilteredData(filtered);
    }
  }, [allData, performClientSideSearch]);

  // Handle filter dengan button "Buka Data"
  const handleFilter = useCallback(() => {
    pageRef.current = 1;
    setCurrentPage(1);
    searchRef.current = "";
    setSearchValue("");
    fetchData();
  }, [fetchData]);

  // Handle search input
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch],
  );

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue("");
    searchRef.current = "";
    setFilteredData(allData);
    setCurrentPage(1);
    pageRef.current = 1;

    toast.info("Pencarian dibersihkan", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [allData]);

  // Handle page change
  const handlePageChange = useCallback((page) => {
    pageRef.current = page;
    setCurrentPage(page);
  }, []);

  // Handle per rows change
  const handlePerRowsChange = useCallback((newPerPage) => {
    countRef.current = parseInt(newPerPage === -1 ? 999999999 : newPerPage);
    pageRef.current = 1;
    setCurrentPage(1);
    setCount(newPerPage);
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchData();
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [fetchData]);

  // FUNGSI EXPORT ALL DATA KE EXCEL
  const exportToExcel = useCallback(() => {
    if (!filteredData || filteredData.length === 0) {
      toast.warning("Tidak ada data untuk diexport");
      return;
    }

    try {
      // Mapping data untuk export
      const exportData = filteredData.map((item) => ({
        "No. Invoice": item.invoice_no || "-",
        "No. Quotation": item.quotation_no || "-",
        "Tanggal Invoice": item.invoice_date || "-",
        "Jatuh Tempo": item.due_date || "-",
        "Klien": item.klien_name || "-",
        "Deskripsi Pekerjaan": item.work_desc || "-",
        "No. Kontrak": item.contract_no || "-",
        "Total Amount": item.total_amount || "0",
        "Status": item.status || "draft",
        "Bank Account": item.bank_account || "-",
        "Unit": item.unit || "-",
        "Revisi": item.revisi || "000",
        "No. DP": item.dp_number || "-",
      }));

      // Membuat headers dari key object pertama
      const headers = Object.keys(exportData[0]);

      // Generate HTML table untuk Excel
      let html = `
        <html>
          <head>
            <meta charset="UTF-8">
            <title>Monitoring Invoice</title>
            <style>
              table { border-collapse: collapse; width: 100%; }
              th { background-color: #4a6fa5; color: white; font-weight: bold; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              tr:nth-child(even) { background-color: #f2f2f2; }
            </style>
          </head>
          <body>
            <h2>Monitoring Invoice & Quotation</h2>
            <p>Periode: ${new Date(values.Bulan).toLocaleDateString("id-ID", { month: "long", year: "numeric" })}</p>
            ${searchValue ? `<p>Hasil Pencarian: "${searchValue}"</p>` : ""}
            <p>Total Data: ${filteredData.length} records</p>
            <p>Tanggal Export: ${new Date().toLocaleDateString("id-ID")}</p>
            <table border="1">
              <tr>
      `;

      // Header
      headers.forEach((header) => {
        html += `<th>${header}</th>`;
      });
      html += `</tr>`;

      // Data rows
      exportData.forEach((row) => {
        html += `<tr>`;
        headers.forEach((header) => {
          const cell = row[header] || "-";
          html += `<td>${cell}</td>`;
        });
        html += `</tr>`;
      });

      html += `
            </table>
          </body>
        </html>
      `;

      // Membuat blob dan download
      const blob = new Blob([html], {
        type: "application/vnd.ms-excel",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      // Generate filename
      const periodeStr = new Date(values.Bulan)
        .toLocaleDateString("id-ID", { month: "2-digit", year: "numeric" })
        .replace(/\//g, "-");
      
      const filename = searchValue
        ? `Monitoring_Invoice_${searchValue}_${periodeStr}_${new Date().toISOString().split("T")[0]}.xls`
        : `Monitoring_Invoice_${periodeStr}_${new Date().toISOString().split("T")[0]}.xls`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(
        `Berhasil mengexport ${filteredData.length} data ke Excel`,
        {
          position: "top-center",
          autoClose: 2000,
        }
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Gagal mengexport data", {
        position: "top-center",
        autoClose: 2000,
      });
    }
  }, [filteredData, searchValue, values.Bulan]);

  // Format tanggal helper
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    try {
      if (dateString.includes("-") && dateString.split("-").length === 3) {
        return dateString;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (!amount || amount === "0") return "Rp 0";
    const num = parseFloat(amount);
    if (isNaN(num)) return amount;
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 2,
    }).format(num);
  }, []);

  // Data untuk tabel (dengan pagination client-side)
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * count;
    const endIndex = startIndex + count;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, count]);

  // Get status color
  const getStatusColor = useCallback((status) => {
    const statusMap = {
      draft: "bg-yellow-100 text-yellow-800 border border-yellow-200",
      sent: "bg-blue-100 text-blue-800 border border-blue-200",
      done: "bg-green-100 text-green-800 border border-green-200",
      overdue: "bg-red-100 text-red-800 border border-red-200",
      cancelled: "bg-gray-100 text-gray-800 border border-gray-200",
    };
    return (
      statusMap[status?.toLowerCase()] ||
      "bg-gray-100 text-gray-800 border border-gray-200"
    );
  }, []);

  // Memoized columns - HANYA dengan tombol DETAIL
  const columns = useMemo(
    () => [
      {
        name: "No",
        selector: (_row, index) => (currentPage - 1) * count + index + 1,
        sortable: false,
        width: "60px",
        center: true,
      },
      {
        name: "No. Invoice",
        selector: (row) => row.invoice_no,
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <div className="text-xs font-mono whitespace-normal break-all">
            <span className="font-semibold text-blue-600">
              {row.invoice_no || "-"}
            </span>
          </div>
        ),
      },
      {
        name: "No. Quotation",
        selector: (row) => row.quotation_no,
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-all">
            {row.quotation_no || "-"}
          </div>
        ),
      },
      {
        name: "Tanggal",
        selector: (row) => row.invoice_date,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <div className="text-xs whitespace-nowrap">
            <div>{formatDate(row.invoice_date)}</div>
            <div className="text-gray-400 text-[10px]">
              Due: {formatDate(row.due_date)}
            </div>
          </div>
        ),
      },
      {
        name: "Klien",
        selector: (row) => row.klien_name,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            <div className="font-medium">{row.klien_name || "-"}</div>
          </div>
        ),
      },
      {
        name: "Deskripsi Pekerjaan",
        selector: (row) => row.work_desc,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.work_desc || "-"}
            {row.contract_no && (
              <div className="text-gray-400 text-[10px] mt-1">
                Kontrak: {row.contract_no}
              </div>
            )}
          </div>
        ),
      },
      {
        name: "Total Amount",
        selector: (row) => row.total_amount,
        sortable: true,
        minWidth: "130px",
        cell: (row) => (
          <div className="text-xs font-semibold text-right w-full">
            {formatCurrency(row.total_amount)}
          </div>
        ),
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        width: "100px",
        cell: (row) => {
          const status = row.status || "sent";
          return (
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(status)}`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          );
        },
      },
      {
        name: "Bank Account",
        selector: (row) => row.bank_account,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.bank_account || "-"}
          </div>
        ),
      },
        {
        name: "Revisi",
        selector: (row) => row.revisi,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.revisi || "-"}
          </div>
        ),
      },
      {
        name: "Aksi",
        cell: (row) => (
          <div className="flex justify-center">
            {/* Tombol Detail */}
            <button
              onClick={(e) => handleDetailClick(row, e)}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors duration-200 text-xs font-medium shadow-sm"
              title="Lihat Detail"
            >
              <BiDetail size={14} />
              <span>Detail</span>
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        width: "150px", // Lebih lebar
        center: true,
      },
    ],
    [
      currentPage,
      count,
      formatDate,
      formatCurrency,
      getStatusColor,
      handleDetailClick,
    ],
  );

  // Subheader component (search dan action buttons) - DITAMBAH TOMBOL EXPORT ALL
  const subHeaderComponentMemo = useMemo(
    () => (
      <div className="w-full mb-4">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-0">
          <div className="flex items-center gap-2 w-full md:w-auto">
            <div className="relative w-full md:w-80">
              <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Cari No. Invoice, Quotation, Klien, Deskripsi..."
                value={searchValue}
                onChange={handleSearchChange}
                disabled={!hasData || isLoading}
                className="pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full transition-colors duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
              {searchValue && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>

            <button
              onClick={handleRefresh}
              disabled={isLoading}
              className="flex items-center px-4 py-2.5 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 active:scale-95 shadow-sm"
              title="Refresh Data"
            >
              <BiRefresh
                className={`mr-2 ${isLoading ? "animate-spin" : ""}`}
              />
              Refresh
            </button>

            {/* TOMBOL EXPORT ALL */}
            <button
              onClick={exportToExcel}
              disabled={isLoading || !hasData || filteredData.length === 0}
              className="flex items-center px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 active:scale-95 shadow-sm"
              title="Export Semua Data ke Excel"
            >
              <BsFileEarmarkExcelFill className="mr-2" size={16} />
              <span>Export All</span>
            </button>
          </div>
        </div>

        {/* Info pencarian */}
        {searchValue && (
          <div className="mt-3 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center text-sm">
              <BiFilter className="text-blue-500 mr-2" />
              <span className="text-blue-700 font-medium">
                Pencarian: "{searchValue}"
              </span>
              <span className="mx-2 text-gray-400">•</span>
              <span className="text-blue-600">
                Menemukan {filteredData.length} dari {allData.length} data
              </span>
              {filteredData.length > 0 &&
                filteredData.length !== allData.length && (
                  <>
                    <span className="mx-2 text-gray-400">•</span>
                    <button
                      onClick={handleClearSearch}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      Clear filter
                    </button>
                  </>
                )}
            </div>
          </div>
        )}
      </div>
    ),
    [
      searchValue,
      handleSearchChange,
      handleClearSearch,
      handleRefresh,
      exportToExcel,
      isLoading,
      hasData,
      filteredData,
      allData,
    ],
  );

  // Custom styles untuk DataTable
  const customStyles = useMemo(
    () => ({
      head: {
        style: {
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
          color: "#374151",
          textTransform: "uppercase",
        },
      },
      headCells: {
        style: {
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "nowrap",
          borderBottom: "2px solid #e5e7eb",
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
        },
      },
      cells: {
        style: {
          fontSize: "12px",
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          minHeight: "60px",
          verticalAlign: "middle",
          borderBottom: "1px solid #f3f4f6",
        },
      },
      rows: {
        style: {
          minHeight: "60px",
          "&:nth-of-type(even)": {
            backgroundColor: "#f9fafb",
          },
          "&:hover": {
            backgroundColor: "#f3f4f6",
            transition: "background-color 0.2s ease",
          },
        },
      },
      progress: {
        style: {
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        },
      },
      pagination: {
        style: {
          fontSize: "12px",
          borderTop: "1px solid #e5e7eb",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    }),
    [],
  );

  // Loading component
  const loadingComponent = useMemo(
    () => (
      <div className="flex flex-col justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <span className="text-gray-600">Memuat data Invoice...</span>
        <span className="text-gray-400 text-sm mt-2">
          Mohon tunggu sebentar
        </span>
      </div>
    ),
    [],
  );

  // No data component
  const noDataComponent = useMemo(() => {
    if (!hasData && isInitialLoad) {
      return (
        <div className="w-full flex flex-col justify-center items-center py-16">
          <FaFileInvoice className="text-5xl text-gray-300 mb-4" />
          <div className="pb-2 text-gray-700 text-lg font-medium">
            Monitoring Invoice & Quotation
          </div>
          <div className="text-gray-500 text-sm text-center mb-6 max-w-md">
            Pilih periode, lalu klik "Buka Data" untuk menampilkan data invoice
          </div>
          <div className="text-xs text-gray-400">
            Data akan ditampilkan berdasarkan periode yang dipilih
          </div>
        </div>
      );
    }

    if (!hasData && !isInitialLoad) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">
            Tidak ada data yang ditemukan
          </div>
        </div>
      );
    }

    if (hasData && searchValue && filteredData.length === 0) {
      return (
        <div className="text-center py-8">
          <div className="text-gray-500 mb-2">
            Tidak ada data yang cocok dengan pencarian "{searchValue}"
          </div>
          <button
            onClick={handleClearSearch}
            className="text-main-100 hover:text-main-200 font-medium"
          >
            Tampilkan semua data
          </button>
        </div>
      );
    }

    return null;
  }, [
    hasData,
    isInitialLoad,
    searchValue,
    filteredData.length,
    handleClearSearch,
  ]);

  // Komponen helper untuk baris detail
  const DetailRow = ({ label, value, children }) => (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">{label}</span>
      {children || (
        <span className="text-sm font-medium mt-1">{value || "-"}</span>
      )}
    </div>
  );

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
          </div>
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card className="shadow-lg">
                {/* Filter Section */}
                <div className="flex flex-row flex-wrap items-start justify-between w-full px-8 py-8 border-b bg-gray-50 rounded-t-lg">
                  <div className="flex flex-col md:flex-row gap-6 w-full md:w-2/3">
                    <div className="w-full md:w-1/2 lg:w-2/5 mb-4 md:mb-0">
                      <div className="flex items-center mb-3">
                        <div className="w-3 h-5 bg-blue-500 rounded mr-3"></div>
                        <label className="block uppercase tracking-wide text-gray-800 text-sm font-bold">
                          Periode Bulan
                        </label>
                      </div>
                      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-1">
                        <HrdInputGenerator
                          type={"bulan"}
                          name={"Bulan"}
                          title={""}
                          value={values}
                          required={true}
                          onChange={customHandler}
                          touched={touched}
                          errors={errors}
                          size={"full"}
                        />
                      </div>
                      {values.Bulan && (
                        <div className="mt-3 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-full inline-block">
                          <span className="text-blue-700 text-xs font-medium">
                            Periode:{" "}
                            {new Date(values.Bulan).toLocaleDateString(
                              "id-ID",
                              {
                                month: "long",
                                year: "numeric",
                              },
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="w-full md:w-1/3 mt-6 md:mt-0">
                    <div className="flex flex-col items-start md:items-end space-y-4">
                      <div className="w-full md:w-auto">
                        <div className="flex items-center mb-2">
                          <div className="w-3 h-5 bg-blue-500 rounded mr-3"></div>
                          <label className="block uppercase tracking-wide text-gray-800 text-sm font-bold">
                            Aksi
                          </label>
                        </div>

                        <div className="flex flex-col space-y-4">
                          <button
                            type="button"
                            onClick={handleFilter}
                            disabled={isLoading}
                            className="inline-flex items-center justify-center text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-6 py-3 text-center transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg w-full md:w-64"
                          >
                            {isLoading ? (
                              <>
                                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-3"></div>
                                <span>Memuat Data...</span>
                              </>
                            ) : (
                              <>
                                <svg
                                  className="w-5 h-5 mr-3"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                  ></path>
                                </svg>
                                <span className="font-semibold">
                                  🔍 Buka Data Invoice
                                </span>
                              </>
                            )}
                          </button>

                          {hasData && (
                            <button
                              type="button"
                              onClick={handleRefresh}
                              disabled={isLoading}
                              className="inline-flex items-center justify-center text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 focus:ring-4 focus:outline-none focus:ring-gray-200 font-medium rounded-lg text-sm px-6 py-2.5 text-center transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow w-full md:w-64"
                            >
                              <svg
                                className="w-4 h-4 mr-3"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                ></path>
                              </svg>
                              <span>Refresh Data</span>
                            </button>
                          )}
                        </div>

                        <div className="mt-4 text-xs text-gray-600 max-w-xs">
                          <div className="flex items-start">
                            <svg
                              className="w-4 h-4 text-blue-500 mr-2 mt-0.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              ></path>
                            </svg>
                            <span>
                              Pilih periode, lalu klik "Buka Data Invoice" untuk
                              menampilkan data
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* DataTable Section */}
                <div className="p-6">
                  {hasData && subHeaderComponentMemo}

                  <div className="overflow-x-auto border rounded-lg shadow-sm">
                    <DataTable
                      columns={columns}
                      data={paginatedData}
                      customStyles={customStyles}
                      progressPending={isLoading}
                      progressComponent={loadingComponent}
                      pagination
                      paginationServer={false}
                      paginationTotalRows={filteredData.length}
                      onChangePage={handlePageChange}
                      onChangeRowsPerPage={handlePerRowsChange}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      paginationPerPage={count}
                      paginationDefaultPage={currentPage}
                      highlightOnHover
                      striped
                      dense
                      noDataComponent={noDataComponent}
                      persistTableHead
                      fixedHeader
                      fixedHeaderScrollHeight="calc(100vh - 400px)"
                      responsive
                      subHeader={hasData}
                      subHeaderWrap
                    />
                  </div>

                  {/* Info tambahan */}
                  {hasData && filteredData.length > 0 && (
                    <div className="mt-4 text-sm text-gray-600">
                      {searchValue ? (
                        <div className="flex items-center justify-between">
                          <div>
                            Menampilkan {filteredData.length} dari{" "}
                            {allData.length} data
                          </div>
                          <button
                            onClick={handleClearSearch}
                            className="text-main-100 hover:text-main-200 font-medium text-sm"
                          >
                            Clear search
                          </button>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-500 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
                          <div className="flex items-center space-x-4">
                            <div>
                              Total Data:{" "}
                              <span className="font-bold text-gray-700">
                                {allData.length}
                              </span>{" "}
                              records
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {values.Bulan && (
                              <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs">
                                Periode:{" "}
                                {new Date(values.Bulan).toLocaleDateString(
                                  "id-ID",
                                  { month: "long", year: "numeric" },
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Detail */}
      <Modal
        size="lg"
        active={detailModal.isOpen}
        toggler={handleCloseDetail}
        className="bg-white"
      >
        <ModalHeader toggler={handleCloseDetail}>
          <div className="flex items-center">
            <FaFileInvoice className="text-blue-600 mr-3" size={24} />
            <div>
              <h3 className="text-xl font-bold text-gray-800">
                Detail Invoice
              </h3>
              <p className="text-sm text-gray-500">
                No. Invoice: {detailModal.data?.invoice_no || "-"}
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {detailModal.data && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-2">
              {/* Kolom Kiri */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">
                    Informasi Invoice
                  </h4>
                  <div className="space-y-3">
                    <DetailRow
                      label="No. Invoice"
                      value={detailModal.data.invoice_no}
                    />
                    <DetailRow
                      label="Tanggal Invoice"
                      value={formatDate(detailModal.data.invoice_date)}
                    />
                    <DetailRow
                      label="Jatuh Tempo"
                      value={formatDate(detailModal.data.due_date)}
                    />
                    <DetailRow label="Status" value={detailModal.data.status}>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(detailModal.data.status)}`}
                      >
                        {detailModal.data.status?.charAt(0).toUpperCase() +
                          detailModal.data.status?.slice(1) || "Draft"}
                      </span>
                    </DetailRow>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">
                    Informasi Klien
                  </h4>
                  <div className="space-y-3">
                    <DetailRow
                      label="Nama Klien"
                      value={detailModal.data.klien_name}
                    />
                    <DetailRow
                      label="No. Quotation"
                      value={detailModal.data.quotation_no}
                    />
                    <DetailRow
                      label="No. Kontrak"
                      value={detailModal.data.contract_no}
                    />
                  </div>
                </div>
              </div>

              {/* Kolom Kanan */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">
                    Detail Pekerjaan
                  </h4>
                  <div className="space-y-3">
                    <div className="flex flex-col">
                      <span className="text-xs text-gray-500">
                        Deskripsi Pekerjaan
                      </span>
                      <span className="text-sm font-medium mt-1">
                        {detailModal.data.work_desc || "-"}
                      </span>
                    </div>
                    <DetailRow label="Unit" value={detailModal.data.unit} />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-600 mb-3 uppercase tracking-wider">
                    Informasi Pembayaran
                  </h4>
                  <div className="space-y-3">
                    <DetailRow
                      label="Total Amount"
                      value={formatCurrency(detailModal.data.total_amount)}
                    />
                    <DetailRow
                      label="Bank Account"
                      value={detailModal.data.bank_account}
                    />
                    <DetailRow
                      label="No. DP"
                      value={detailModal.data.dp_number}
                    />
                    <DetailRow label="Revisi" value={detailModal.data.revisi} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <button
            onClick={handleCloseDetail}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200 font-medium"
          >
            Tutup
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default MonitoringInvoiceQuotation;